import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Wifi,
  Phone,
  Tv,
  CheckCircle,
  Block,
  Cancel,
  Schedule,
  Speed,
  Router,
  VpnKey,
  Computer,
  Info,
  ArrowDropDown,
  MoreVert,
  CancelOutlined,
  SwapHoriz,
  Build,
  LockOpen,
  Map as MapIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { api, ApiResponse } from '../../services/api';
import ContratoServicosService, {
  ContratoServicos,
  CreateContratoServicosData,
  UpdateStatusData
} from '../../services/contratoServicosService';
import ContratoServicosIntForm from '../forms/ContratoServicosIntForm';
import TransferServicoModal from '../modals/TransferServicoModal';
import TabbedContractForm from '../forms/TabbedContractForm';
import { Contract } from '@/types';
import { contractService } from '../../services/contractService';
import { getStatusColor, getStatusLabel, getStatusIcon } from '../../utils/contractStatusUtils';

// Estilo para garantir que o SweetAlert2 apareça na frente do modal
const swal2HighZIndexStyle = `
  .swal2-container-high-zindex {
    z-index: 9999 !important;
  }
`;

// Adicionar o estilo ao documento se ainda não existir
if (typeof document !== 'undefined' && !document.getElementById('swal2-high-zindex-style')) {
  const style = document.createElement('style');
  style.id = 'swal2-high-zindex-style';
  style.textContent = swal2HighZIndexStyle;
  document.head.appendChild(style);
}

interface ContractServicosProps {
  contractId: number;
  contractStatus: 'ativo' | 'bloqueado' | 'cancelado' | 'aguardando_instalacao' | 'aguardando_assinatura' | 'suspenso';
  dadosContrato: Contract;
}

const ContractServicos: React.FC<ContractServicosProps> = ({
  contractId,
  contractStatus,
  dadosContrato
}) => {
  const [servicos, setServicos] = useState<ContratoServicos[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingServico, setEditingServico] = useState<ContratoServicos | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferringServico, setTransferringServico] = useState<ContratoServicos | null>(null);
  const [transferring, setTransferring] = useState(false);
  const [contractFormOpen, setContractFormOpen] = useState(false);
  const [contractFormClientId, setContractFormClientId] = useState<number | null>(null);
  const [contractFormServicoId, setContractFormServicoId] = useState<number | null>(null);
  const [creatingContract, setCreatingContract] = useState(false);
  const [reparando, setReparando] = useState<number | null>(null);
  const [liberandoMac, setLiberandoMac] = useState<number | null>(null);

  useEffect(() => {
    loadServicos();
  }, [contractId]);

  const loadServicos = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ContratoServicosService.getByContractId(contractId);

      if (response.success) {
        setServicos(response.data || []);
      } else {
        setError(response.message || 'Erro ao carregar serviços adicionais');
      }
    } catch (err: any) {
      setError('Erro ao carregar serviços adicionais: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleAddServico = (tipo: string) => {
    setEditingServico(null);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleEditServico = (servico: ContratoServicos) => {
    // Verificar o tipo do serviço para determinar qual formulário usar
    if (servico.tipo === 'internet') {
      // Para serviços de internet, usar o ContratoServicosIntForm
      setEditingServico(servico);
      setDialogOpen(true);
    } else {
      // Para outros tipos, mostrar mensagem de implementação futura
      toast.info(`Edição de serviços do tipo "${servico.tipo}" será implementada em breve!`);
    }
  };

  const handleDeleteServico = async (servico: ContratoServicos) => {
    const result = await Swal.fire({
      title: 'Excluir Serviço',
      text: `Tem certeza que deseja excluir o serviço ${servico.tipo}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d32f2f',
    });

    if (result.isConfirmed) {
      try {
        const response = await ContratoServicosService.delete(servico.id);

        if (response.success) {
          toast.success('Serviço excluído com sucesso!');
          loadServicos();
        } else {
          toast.error(response.message || 'Erro ao excluir serviço');
        }
      } catch (err: any) {
        toast.error('Erro ao excluir serviço: ' + err.message);
      }
    }
  };

  const handleCancelServico = async (servico: ContratoServicos) => {
    const result = await Swal.fire({
      title: 'Cancelar Serviço',
      text: `Tem certeza que deseja cancelar o serviço ${servico.tipo}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, cancelar',
      cancelButtonText: 'Não',
      confirmButtonColor: '#f57c00',
      input: 'textarea',
      inputLabel: 'Motivo do cancelamento (opcional)',
      inputPlaceholder: 'Digite o motivo do cancelamento...',
      inputValidator: (value) => {
        // Validação opcional - pode ser removida se não for obrigatório
        return null;
      }
    });

    if (result.isConfirmed) {
      try {
        const motivo = result.value || 'Cancelamento manual';
        const statusData: UpdateStatusData = {
          status: 'cancelado',
          observacoes: motivo
        };

        const response = await ContratoServicosService.updateStatus(servico.id, statusData);

        if (response.success) {
          toast.success('Serviço cancelado com sucesso!');
          loadServicos();
        } else {
          toast.error(response.message || 'Erro ao cancelar serviço');
        }
      } catch (err: any) {
        toast.error('Erro ao cancelar serviço: ' + err.message);
      }
    }
  };

  const handleAtivarServico = async (servico: ContratoServicos) => {
    const result = await Swal.fire({
      title: 'Ativar Serviço',
      text: `Tem certeza que deseja ativar o serviço ${servico.tipo}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, ativar',
      cancelButtonText: 'Não',
      confirmButtonColor: '#28a745',
    });

    if (result.isConfirmed) {
      try {
        const response = await ContratoServicosService.updateStatus(servico.id, { status: 'ativo' });
        if (response.success) {
          toast.success('Serviço ativado com sucesso!');
          loadServicos();
        } else {
          toast.error(response.message || 'Erro ao ativar serviço');
        }
      } catch (err: any) {
        toast.error('Erro ao ativar serviço: ' + err.message);
      }
    }
  };

  const handleSubmit = async (formData: CreateContratoServicosData) => {
    try {
      setSubmitting(true);

      const response = editingServico
        ? await ContratoServicosService.update(editingServico.id, formData)
        : await ContratoServicosService.create(contractId, formData);

      if (response.success) {
        toast.success(editingServico ? 'Serviço atualizado com sucesso!' : 'Serviço criado com sucesso!');
        setDialogOpen(false);
        loadServicos();
      } else {
        // Verificar se há erros de duplicidade
        if (response.errors && Array.isArray(response.errors)) {
          // Exibir erros de duplicidade específicos
          response.errors.forEach((error: any) => {
            if (error.type === 'unique_violation') {
              toast.error(error.message, {
                autoClose: 8000, // 8 segundos para dar tempo de ler
                style: {
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }
              });
            } else {
              toast.error(error.message || 'Erro de validação');
            }
          });
        } else {
          toast.error(response.message || 'Erro ao salvar serviço');
        }
      }
    } catch (err: any) {
      // Verificar se é um erro de duplicidade do axios
      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        err.response.data.errors.forEach((error: any) => {
          if (error.type === 'unique_violation') {
            toast.error(error.message, {
              autoClose: 8000,
              style: {
                backgroundColor: '#d32f2f',
                color: 'white',
                fontSize: '14px',
                fontWeight: 'bold'
              }
            });
          } else {
            toast.error(error.message || 'Erro de validação');
          }
        });
      } else {
        toast.error('Erro ao salvar serviço: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (servico: ContratoServicos, newStatus: string) => {
    try {
      const statusData: UpdateStatusData = { status: newStatus as any };
      const response = await ContratoServicosService.updateStatus(servico.id, statusData);

      if (response.success) {
        toast.success('Status alterado com sucesso!');
        loadServicos();
      } else {
        toast.error(response.message || 'Erro ao alterar status');
      }
    } catch (err: any) {
      toast.error('Erro ao alterar status: ' + err.message);
    }
  };

  const handleTransferServico = (servico: ContratoServicos) => {
    setTransferringServico(servico);
    setTransferModalOpen(true);
  };

  const handleConfirmTransfer = async (newContractId: number) => {
    if (!transferringServico) return;

    try {
      setTransferring(true);

      const response = await ContratoServicosService.transferir(transferringServico.id, newContractId);

      if (response.success) {
        await Swal.fire({
          title: 'Sucesso!',
          text: 'Serviço transferido com sucesso!',
          icon: 'success',
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            container: 'swal2-container-high-zindex'
          }
        });
        setTransferModalOpen(false);
        setTransferringServico(null);
        loadServicos();
      } else {
        await Swal.fire({
          title: 'Erro!',
          text: response.message || 'Erro ao transferir serviço',
          icon: 'error',
          customClass: {
            container: 'swal2-container-high-zindex'
          }
        });
      }
    } catch (err: any) {
      await Swal.fire({
        title: 'Erro!',
        text: 'Erro ao transferir serviço: ' + err.message,
        icon: 'error',
        customClass: {
          container: 'swal2-container-high-zindex'
        }
      });
    } finally {
      setTransferring(false);
    }
  };

  const handleCloseTransferModal = () => {
    if (!transferring) {
      setTransferModalOpen(false);
      setTransferringServico(null);
    }
  };

  const handleCreateNewContract = (clientId: number) => {
    if (!transferringServico) return;

    setContractFormClientId(clientId);
    setContractFormServicoId(transferringServico.id);
    setTransferModalOpen(false);
    setContractFormOpen(true);
  };

  const handleContractFormSubmit = async (data: Partial<Contract>) => {
    if (!contractFormClientId || !contractFormServicoId) return;

    try {
      setCreatingContract(true);

      const response = await contractService.create({
        ...data,
        cliente_id: contractFormClientId,
        servico_id: contractFormServicoId,
      } as any);

      if (response.success) {
        toast.success('Contrato criado e serviço transferido com sucesso!');
        setContractFormOpen(false);
        setContractFormClientId(null);
        setContractFormServicoId(null);
        setTransferringServico(null);
        loadServicos();
      } else {
        toast.error(response.message || 'Erro ao criar contrato');
      }
    } catch (err: any) {
      toast.error('Erro ao criar contrato: ' + (err.response?.data?.message || err.message));
    } finally {
      setCreatingContract(false);
    }
  };

  const handleCloseContractForm = () => {
    if (!creatingContract) {
      setContractFormOpen(false);
      setContractFormClientId(null);
      setContractFormServicoId(null);
      setTransferringServico(null);
    }
  };

  const handleRepararConexao = async (servico: ContratoServicos) => {
    const result = await Swal.fire({
      title: 'Reparar Conexão',
      html: `<p>Deseja reparar a conexão do serviço <strong>${servico.usuario_pppoe || 'sem PPPoE'}</strong>?</p>
             <p style="font-size: 0.85rem; color: #666;">Esta ação irá:</p>
             <ul style="text-align: left; font-size: 0.85rem; color: #666;">
               <li>Derrubar a conexão ativa no RADIUS</li>
               <li>Recriar o usuário no RADIUS</li>
               ${servico.nas_server ? '<li>Derrubar a conexão no MikroTik (se API ativa)</li><li>Recriar o secret no MikroTik (se API ativa)</li>' : ''}
             </ul>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, reparar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1976d2',
      customClass: {
        container: 'swal2-container-high-zindex'
      }
    });

    if (result.isConfirmed) {
      try {
        setReparando(servico.id);
        const response = await ContratoServicosService.repararConexao(servico.id);

        if (response.success) {
          // Montar detalhes para exibir
          const detalhes = response.data;
          let detalhesHtml = '<div style="text-align: left; font-size: 0.9rem;">';

          if (detalhes?.radius) {
            const radiusIcon = detalhes.radius.success ? '✅' : '❌';
            detalhesHtml += `<p>${radiusIcon} <strong>RADIUS:</strong> ${detalhes.radius.message}</p>`;
          }

          if (detalhes?.mikrotik) {
            const mkIcon = detalhes.mikrotik.success ? '✅' : '❌';
            detalhesHtml += `<p>${mkIcon} <strong>MikroTik:</strong> ${detalhes.mikrotik.message}</p>`;
          }

          detalhesHtml += '</div>';

          await Swal.fire({
            title: 'Conexão Reparada!',
            html: detalhesHtml,
            icon: 'success',
            customClass: {
              container: 'swal2-container-high-zindex'
            }
          });
        } else {
          // Montar detalhes de erro
          const detalhes = response.data;
          let detalhesHtml = `<p>${response.message}</p><div style="text-align: left; font-size: 0.9rem; margin-top: 10px;">`;

          if (detalhes?.radius) {
            const radiusIcon = detalhes.radius.success ? '✅' : '❌';
            detalhesHtml += `<p>${radiusIcon} <strong>RADIUS:</strong> ${detalhes.radius.message}</p>`;
          }

          if (detalhes?.mikrotik) {
            const mkIcon = detalhes.mikrotik.success ? '✅' : '❌';
            detalhesHtml += `<p>${mkIcon} <strong>MikroTik:</strong> ${detalhes.mikrotik.message}</p>`;
          }

          detalhesHtml += '</div>';

          await Swal.fire({
            title: 'Reparação com Erros',
            html: detalhesHtml,
            icon: 'warning',
            customClass: {
              container: 'swal2-container-high-zindex'
            }
          });
        }
      } catch (err: any) {
        await Swal.fire({
          title: 'Erro!',
          text: err.message || 'Erro ao reparar conexão',
          icon: 'error',
          customClass: {
            container: 'swal2-container-high-zindex'
          }
        });
      } finally {
        setReparando(null);
      }
    }
  };

  const handleLiberarMac = async (servico: ContratoServicos) => {
    const result = await Swal.fire({
      title: 'Liberar MAC',
      html: `<p>Deseja liberar o MAC address do serviço <strong>${servico.usuario_pppoe}</strong>?</p>
             <p style="font-size: 0.85rem; color: #666;">Esta ação irá:</p>
             <ul style="text-align: left; font-size: 0.85rem; color: #666;">
               <li>Limpar o campo Endereço MAC no sistema</li>
               <li>Remover o vínculo de MAC no RADIUS</li>
               <li>Derrubar a conexão ativa para permitir nova autenticação</li>
             </ul>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, liberar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d32f2f',
      customClass: {
        container: 'swal2-container-high-zindex'
      }
    });

    if (result.isConfirmed) {
      try {
        setLiberandoMac(servico.id);
        const response = await ContratoServicosService.liberarMac(servico.id);

        if (response.success) {
          toast.success(response.message || 'MAC liberado com sucesso!');
          loadServicos();
        } else {
          toast.error(response.message || 'Erro ao liberar MAC');
        }
      } catch (err: any) {
        toast.error(err.message || 'Erro ao liberar MAC');
      } finally {
        setLiberandoMac(null);
      }
    }
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'internet':
        return <Wifi />;
      case 'telefonia':
        return <Phone />;
      case 'tv':
        return <Tv />;
      default:
        return <Info />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight="bold">
          Serviços Adicionais
        </Typography>
        <Box>
          <Button
            variant="contained"
            endIcon={<ArrowDropDown />}
            onClick={handleMenuOpen}
            sx={{ borderRadius: 2 }}
            disabled={contractStatus === 'cancelado'}
          >
            Adicionar Serviço
          </Button>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => handleAddServico('internet')}>
              <ListItemIcon>
                <Wifi />
              </ListItemIcon>
              <ListItemText primary="Internet" />
            </MenuItem>
            <MenuItem disabled>
              <ListItemIcon>
                <Tv />
              </ListItemIcon>
              <ListItemText
                primary="TV"
                secondary="Em breve"
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            </MenuItem>
            <MenuItem disabled>
              <ListItemIcon>
                <MoreVert />
              </ListItemIcon>
              <ListItemText
                primary="Outros"
                secondary="Em breve"
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Lista de Serviços */}
      {servicos.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                Nenhum serviço adicional cadastrado
              </Typography>
              <Button
                variant="outlined"
                endIcon={<ArrowDropDown />}
                onClick={handleMenuOpen}
                disabled={contractStatus === 'cancelado'}
                sx={{ mt: 2 }}
              >
                Adicionar Primeiro Serviço
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tipo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Conexão</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Usuário PPPoE</TableCell>
                <TableCell>IP</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Endereço Instalação</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {servicos.map((servico) => (
                <TableRow key={servico.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getTipoIcon(servico.tipo)}
                      <Typography variant="body2" textTransform="capitalize">
                        {servico.tipo}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(servico.status)}
                      color={getStatusColor(servico.status) as any}
                      icon={getStatusIcon(servico.status, 'small')}
                      size="small"
                    />
                    {servico.isento && (
                      <>
                        <span style={{ marginLeft: 5, marginRight: 5 }}>-</span>
                        <Chip
                          label="Isento"
                          color="warning"
                          size="small"
                          variant="outlined"
                        />
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {servico.tipo === 'internet' && servico.usuario_pppoe ? (
                      <Chip
                        label={servico.conexao === 'Online' ? 'Online' : 'Offline'}
                        color={servico.conexao === 'Online' ? 'success' : 'error'}
                        size="small"
                      //variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {servico.plano?.nome || 'Não definido'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box display="flex" flexDirection="column" gap={0}>
                      <Typography variant="caption">{servico.valor ? `Valor: R$ ${servico.valor}` : '-'}</Typography>
                      <Typography variant="caption">{servico.acrescimo ? `Acréscimo: R$ ${servico.acrescimo}` : '-'}</Typography>
                      <Typography variant="caption">{servico.desconto ? `Desconto: R$ ${servico.desconto}` : '-'}</Typography>
                      <Typography fontWeight="bold" variant="body2">{servico.valor ? `Total: R$ ${parseFloat(servico.valor.toString()) + parseFloat(servico.acrescimo?.toString() || '0') - parseFloat(servico.desconto?.toString() || '0')}` : '-'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {servico.usuario_pppoe || '-'}
                    </Typography>
                    <Typography variant="caption">
                      {servico.senha_pppoe || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {servico.endereco_ip || '-'}
                    </Typography>
                    <Typography variant="caption">
                      {servico.endereco_mac || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {servico.endereco_inst ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box>
                          <Typography variant="body2">
                            {servico.endereco_inst}, {servico.numero_inst}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {servico.bairro_inst} - {servico.cidade_inst}/{servico.estado_inst}
                          </Typography>
                        </Box>
                        {servico.coordenadas_inst && (
                          <Tooltip title="Abrir no Google Maps">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => window.open(`https://www.google.com/maps?q=${servico.coordenadas_inst}`, '_blank')}
                            >
                              <MapIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }} align="center">
                    <Box display="flex" gap={1} justifyContent="center">
                      <Tooltip title={servico.tipo === 'internet' ? 'Editar' : 'Edição em breve'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleEditServico(servico)}
                            disabled={servico.tipo !== 'internet' || contractStatus === 'cancelado'}
                            sx={{
                              opacity: servico.tipo === 'internet' ? 1 : 0.5,
                              '&:hover': {
                                opacity: servico.tipo === 'internet' ? 0.8 : 0.5,
                              }
                            }}
                          >
                            <Edit />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {servico.status !== 'cancelado' && (
                        <Tooltip title="Cancelar Serviço">
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleCancelServico(servico)}
                            >
                              <CancelOutlined />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {['cancelado', 'aguardando_instalacao', 'aguardando_assinatura'].includes(servico.status) && (
                        <Tooltip title="Ativar Serviço">
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={contractStatus === 'cancelado'}
                              onClick={() => handleAtivarServico(servico)}
                            >
                              <CheckCircle />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Reparar Conexão">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={
                              !servico.usuario_pppoe ||
                              servico.status === 'cancelado' ||
                              contractStatus === 'cancelado' ||
                              reparando === servico.id
                            }
                            onClick={() => handleRepararConexao(servico)}
                          >
                            {reparando === servico.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Build />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Liberar MAC">
                        <span>
                          <IconButton
                            size="small"
                            color="secondary"
                            disabled={
                              !servico.usuario_pppoe ||
                              servico.status === 'cancelado' ||
                              contractStatus === 'cancelado' ||
                              liberandoMac === servico.id
                            }
                            onClick={() => handleLiberarMac(servico)}
                          >
                            {liberandoMac === servico.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <LockOpen />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Transferir para outro contrato">
                        <span>
                          <IconButton
                            size="small"
                            color="info"
                            disabled={contractStatus === 'cancelado'}
                            onClick={() => handleTransferServico(servico)}
                          >
                            <SwapHoriz />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={true}
                            onClick={() => handleDeleteServico(servico)}
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal de Adição/Edição */}
      <ContratoServicosIntForm
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        editingServico={editingServico}
        submitting={submitting}
        escritorio_id={dadosContrato?.escritorio_id}
        contractAddress={dadosContrato ? {
          cep_inst: dadosContrato.cep_inst,
          cidade_inst: dadosContrato.cidade_inst,
          estado_inst: dadosContrato.estado_inst,
          bairro_inst: dadosContrato.bairro_inst,
          endereco_inst: dadosContrato.endereco_inst,
          numero_inst: dadosContrato.numero_inst,
          complemento_inst: dadosContrato.complemento_inst,
          coordenadas_inst: '',
          referencia_inst: dadosContrato.referencia_inst,
        } : undefined}
      />

      {/* Modal de Transferência */}
      <TransferServicoModal
        open={transferModalOpen}
        onClose={handleCloseTransferModal}
        onConfirm={handleConfirmTransfer}
        onCreateNewContract={handleCreateNewContract}
        servicoTipo={transferringServico?.tipo || ''}
        servicoId={transferringServico?.id || 0}
        loading={transferring}
      />

      {/* Modal de Criação de Contrato para Transferir Serviço */}
      {contractFormClientId && (
        <TabbedContractForm
          open={contractFormOpen}
          onClose={handleCloseContractForm}
          onSubmit={handleContractFormSubmit}
          clientId={contractFormClientId}
          servicoId={contractFormServicoId || undefined}
          loading={creatingContract}
        />
      )}
    </Box>
  );
};

export default ContractServicos;
